import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ServiceFormDialog from "./ServiceFormDialog";

const API = process.env.REACT_APP_API_URL;

const isShowingInBooking = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

/* ---------- generic dialog (same markup as the existing category dialog) ---------- */
const Dialog = ({ title, onClose, children, footer, width = "500px" }) => (
  <>
    <div className="modal-backdrop fade show" onClick={onClose} />
    <div className="modal d-block" role="dialog" aria-modal="true">
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: width, width: "95%" }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="fs-5 modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
          {children}
          {footer}
        </div>
      </div>
    </div>
  </>
);

/* ---------- Add / Edit Subcategory dialog ---------- */
const SubcategoryDialog = ({
  token,
  categoryId,
  categories, // optional: when given (and adding), the user picks the category
  subcategory,
  onClose,
  onSaved,
}) => {
  const isEdit = !!subcategory;
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    categoryId ? String(categoryId) : "",
  );
  const [name, setName] = useState(subcategory?.name || "");
  const [nameAr, setNameAr] = useState(subcategory?.name_ar || "");
  const [description, setDescription] = useState(
    subcategory?.description || "",
  );
  const [descriptionAr, setDescriptionAr] = useState(
    subcategory?.description_ar || "",
  );
  const [showInBooking, setShowInBooking] = useState(
    isEdit ? isShowingInBooking(subcategory.is_showing_in_booking) : true,
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !selectedCategoryId)
      return toast.error("Category is required");
    if (!name.trim()) return toast.error("Subcategory name is required");

    const formData = new FormData();
    if (!isEdit) formData.append("category_id", selectedCategoryId); // parent can't change on edit
    formData.append("name", name.trim());
    formData.append("name_ar", nameAr.trim());
    formData.append("description", description.trim());
    formData.append("description_ar", descriptionAr.trim());
    formData.append("is_showing_in_booking", String(showInBooking));

    setSaving(true);
    try {
      const response = await fetch(
        isEdit
          ? `${API}api/admin/service-subcategories/${subcategory.id}`
          : `${API}api/admin/service-subcategories`,
        {
          method: isEdit ? "PUT" : "POST",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const json = await response.json();
      if (response.ok) {
        toast.success(isEdit ? "Subcategory updated" : "Subcategory added");
        onSaved();
      } else {
        toast.error(json.errorMessage || "Failed to save subcategory");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while saving the subcategory");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      title={isEdit ? "Edit Subcategory" : "Add Subcategory"}
      onClose={onClose}
      width="700px"
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          {!isEdit && categories && (
            <div className="mb-3">
              <label className="form-label">
                Category <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">
                Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter subcategory name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label
                className="form-label w-100 text-end"
                htmlFor="subcategory-name-ar"
              >
                الاسم
              </label>
              <input
                id="subcategory-name-ar"
                type="text"
                className="form-control"
                placeholder="أدخل اسم الفئة الفرعية"
                dir="rtl"
                value={nameAr}
                onChange={(e) => setNameAr(e.target.value)}
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows="3"
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label
              className="form-label w-100 text-end"
              htmlFor="subcategory-description-ar"
            >
              الوصف
            </label>
            <textarea
              id="subcategory-description-ar"
              className="form-control"
              rows="3"
              placeholder="أدخل الوصف"
              dir="rtl"
              value={descriptionAr}
              onChange={(e) => setDescriptionAr(e.target.value)}
            />
          </div>
          <div className="form-check d-flex align-items-center gap-2">
            <input
              id="subcategory-showing-in-booking"
              type="checkbox"
              className="form-check-input m-0"
              checked={showInBooking}
              onChange={(e) => setShowInBooking(e.target.checked)}
            />
            <label
              className="form-check-label m-0"
              htmlFor="subcategory-showing-in-booking"
            >
              Show in Booking
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary-600"
            disabled={saving}
          >
            {isEdit ? "Save Changes" : "Add Subcategory"}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

/* ---------- Delete Subcategory dialog ---------- */
const DeleteSubcategoryDialog = ({
  token,
  subcategory,
  categoryName,
  onClose,
  onDeleted,
}) => {
  const [deleting, setDeleting] = useState(false);
  const count = subcategory.services_count ?? 0;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(
        `${API}api/admin/service-subcategories/${subcategory.id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } },
      );
      const json = await response.json().catch(() => ({}));
      if (response.ok) {
        const moved =
          json.data?.services_moved_to_category ??
          json.services_moved_to_category ??
          count;
        toast.success(
          `Subcategory deleted. ${moved} service${moved === 1 ? "" : "s"} moved directly under ${categoryName}.`,
        );
        onDeleted();
      } else {
        toast.error(json.errorMessage || "Failed to delete subcategory");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while deleting the subcategory");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      title="Delete Subcategory"
      onClose={deleting ? () => {} : onClose}
      footer={
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-light"
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger-600"
            disabled={deleting}
            onClick={handleDelete}
          >
            {deleting ? "Deleting..." : "Delete Subcategory"}
          </button>
        </div>
      }
    >
      <div className="modal-body">
        <p className="mb-8">
          Delete <strong>{subcategory.name}</strong>?
        </p>
        <p className="mb-0 text-secondary-light">
          The services inside are <strong>not deleted</strong>.{" "}
          {count > 0
            ? `${count} service${count === 1 ? "" : "s"} will move directly under ${categoryName}.`
            : "It has no services."}
        </p>
      </div>
    </Dialog>
  );
};

/* ---------- Delete Service dialog ---------- */
const DeleteServiceDialog = ({ token, service, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API}api/admin/services/${service.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Service deleted");
        onDeleted();
      } else {
        const json = await response.json().catch(() => ({}));
        toast.error(json.errorMessage || "Failed to delete service");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while deleting the service");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      title="Delete Service"
      onClose={deleting ? () => {} : onClose}
      footer={
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-light"
            onClick={onClose}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger-600"
            disabled={deleting}
            onClick={handleDelete}
          >
            {deleting ? "Deleting..." : "Delete Service"}
          </button>
        </div>
      }
    >
      <div className="modal-body">
        <p className="mb-8">
          Delete <strong>{service.name}</strong>?
        </p>
        <p className="mb-0 text-secondary-light">
          You won't be able to revert this.
        </p>
      </div>
    </Dialog>
  );
};

/* ---------- services table (used for each group) ---------- */
const ServiceRows = ({ services, onEdit, onDelete }) => {
  if (services.length === 0) {
    return (
      <div className="px-24 py-12 text-secondary-light text-sm">
        No services
      </div>
    );
  }

  return (
    <div className="table-responsive scroll-sm">
      <table
        className="table bordered-table sm-table mb-0"
        style={{ tableLayout: "fixed", width: "100%" }}
      >
        <colgroup>
          {/* <col style={{ width: "70px" }} /> */}
          <col style={{ width: "25%" }} />
          <col style={{ width: "25%" }} />
          <col style={{ width: "160px" }} />
          <col style={{ width: "140px" }} />
        </colgroup>

        <tbody>
          {services.map((service) => (
            <tr key={service.id}>
              {/* <td>{service.id}</td> */}

              {/* English Name & Description */}
              <td>
                <div className="text-start">{service.name}</div>

                {service.description && (
                  <div className="text-secondary-light text-sm text-start mt-2">
                    {service.description}
                  </div>
                )}
              </td>

              {/* Arabic Name & Description */}
              <td className="ps-5">
                {service.name_ar && (
                  <div className="text-secondary-light text-end">
                    {service.name_ar}
                  </div>
                )}

                {service.description_ar && (
                  <div className="text-secondary-light text-sm text-end mt-2">
                    {service.description_ar}
                  </div>
                )}
              </td>

              {/* Duration */}
              <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                {service.durationMinutes
                  ? `${service.durationMinutes} min`
                  : "-"}
              </td>

              {/* Actions */}
              <td className="text-center">
                <div className="d-flex align-items-center gap-10 justify-content-center">
                  <button
                    onClick={() => onEdit(service)}
                    className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                    title="Edit service"
                  >
                    <Icon icon="lucide:edit" className="menu-icon" />
                  </button>

                  <button
                    onClick={() => onDelete(service)}
                    className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                    title="Delete service"
                  >
                    <Icon
                      icon="fluent:delete-24-regular"
                      className="menu-icon"
                    />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
/* ---------- main screen ---------- */
const SingleCategoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  // keys of collapsed groups: subcategory ids (as strings) and "direct"
  const [collapsed, setCollapsed] = useState(() => new Set());
  // dialog = { type: "service", subcategoryId?, service? (edit) } | { type: "subcategory", subcategory? } | { type: "deleteSubcategory", subcategory } | { type: "deleteService", service }

  const category = categories.find((c) => String(c.id) === String(id));
  const headers = { Authorization: `Bearer ${token}` };

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [catRes, subRes, svcRes] = await Promise.all([
        fetch(`${API}api/admin/service-categories?limit=100`, { headers }),
        fetch(
          `${API}api/admin/service-subcategories?category_id=${id}&limit=100`,
          { headers },
        ),
        fetch(`${API}api/admin/services?category_id=${id}&limit=100`, {
          headers,
        }),
      ]);
      const [catJson, subJson, svcJson] = await Promise.all([
        catRes.json(),
        subRes.json(),
        svcRes.json(),
      ]);
      if (catRes.ok) setCategories(catJson.data.categories || []);
      if (subRes.ok) setSubcategories(subJson.data.subcategories || []);
      if (svcRes.ok) setServices(svcJson.data.services || []);
    } catch (error) {
      console.error("Error loading category detail:", error);
      toast.error("Failed to load category");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const closeDialog = () => setDialog(null);
  const savedAndReload = () => {
    closeDialog();
    loadAll();
  };

  const DIRECT_KEY = "direct";
  const isOpen = (key) => !collapsed.has(String(key));
  const toggleGroup = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      const k = String(key);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () =>
    setCollapsed(
      new Set([...subcategories.map((s) => String(s.id)), DIRECT_KEY]),
    );

  const directServices = services.filter((s) => !s.subcategory);
  const servicesIn = (subId) =>
    services.filter((s) => String(s.subcategory?.id) === String(subId));

  return (
    <>
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-sm btn-light d-flex align-items-center"
              onClick={() => navigate("/service-catalog")}
              title="Back to catalog"
            >
              <Icon icon="ion:arrow-back" />
            </button>
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <h6 className="text-md mb-0">{category?.name || "Category"}</h6>
                {category?.name_ar && (
                  <h6 className="text-md mb-0" dir="rtl">
                    {category.name_ar}
                  </h6>
                )}
              </div>
              {category?.description && (
                <div className="text-sm text-secondary-light">
                  {category?.description}
                </div>
              )}
              {category?.description_ar && (
                <div className="text-sm mb-1 text-secondary-light">
                  {category?.description_ar}
                </div>
              )}
              <span className="text-sm text-secondary-light">
                {subcategories.length} subcategor
                {subcategories.length === 1 ? "y" : "ies"} · {services.length}{" "}
                service
                {services.length === 1 ? "" : "s"}
                {category && (
                  <>
                    {" "}
                    ·{" "}
                    {isShowingInBooking(category.is_showing_in_booking)
                      ? "In booking"
                      : "Hidden"}
                  </>
                )}
              </span>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary-600"
              onClick={() => setDialog({ type: "subcategory" })}
            >
              <i className="ri-add-line" /> Add Subcategory
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary-600"
              onClick={() => setDialog({ type: "service" })}
            >
              <i className="ri-add-line" /> Add Service
            </button>
          </div>
        </div>
        {/* <div className="card-body p-24 !pb-0">
          {(category?.description || category?.description_ar) && (
            <div className="d-flex justify-content-between gap-4 pb-3 border-bottom">
              <div className="flex-grow-1 text-start">
                {category?.description && (
                  <>
                    <div className="fw-semibold mb-1">Description</div>
                    <div className="text-secondary-light">
                      {category.description}
                    </div>
                  </>
                )}
              </div>
              <div
                className="flex-grow-1 text-end"
                style={{ textAlign: "right" }}
              >
                {category?.description_ar && (
                  <>
                    <div className="fw-semibold mb-1">الوصف</div>
                    <div className="text-secondary-light">
                      {category.description_ar}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div> */}
        <div className="card-body p-24">
          {loading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <>
              {/* Subcategories → services */}
              {subcategories.length === 0 && (
                <div className="text-secondary-light mb-20">
                  No subcategories yet. Services can sit directly under this
                  category.
                </div>
              )}

              {subcategories.map((sub) => {
                const subServices = servicesIn(sub.id);
                return (
                  <div
                    key={sub.id}
                    className="border radius-8 mb-16 overflow-hidden"
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 bg-neutral-50 px-24 py-12">
                      <div
                        className="d-flex align-items-center gap-2 flex-grow-1"
                        style={{ cursor: "pointer" }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen(sub.id)}
                        onClick={() => toggleGroup(sub.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleGroup(sub.id);
                          }
                        }}
                      >
                        <Icon
                          icon={
                            isOpen(sub.id)
                              ? "mdi:chevron-down"
                              : "mdi:chevron-right"
                          }
                          className="text-secondary-light text-xl"
                        />
                        <div>
                          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <span className="fw-semibold">{sub.name}</span>
                            {sub.name_ar && (
                              <span className="fw-semibold" dir="rtl">
                                {sub.name_ar}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-secondary-light">
                            {sub.description}
                          </div>
                          <div className="text-sm text-secondary-light mb-1">
                            {sub.description_ar}
                          </div>
                          <div className="text-sm text-secondary-light">
                            {sub.services_count ?? subServices.length} services{" "}
                            <span
                              className={`badge text-sm ${
                                isShowingInBooking(sub.is_showing_in_booking)
                                  ? "bg-success-focus text-success-600"
                                  : "bg-neutral-200 text-secondary-light"
                              }`}
                            >
                              {isShowingInBooking(sub.is_showing_in_booking)
                                ? "In booking"
                                : "Hidden"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="bg-primary-600 bg-hover-primary-700 text-white border border-primary-600 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                          title={`Add service to ${sub.name}`}
                          onClick={() =>
                            setDialog({
                              type: "service",
                              subcategoryId: sub.id,
                            })
                          }
                        >
                          <Icon icon="ic:round-plus" />
                        </button>
                        <button
                          type="button"
                          className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Edit subcategory"
                          onClick={() =>
                            setDialog({ type: "subcategory", subcategory: sub })
                          }
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        <button
                          type="button"
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                          title="Delete subcategory"
                          onClick={() =>
                            setDialog({
                              type: "deleteSubcategory",
                              subcategory: sub,
                            })
                          }
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>
                      </div>
                    </div>
                    {isOpen(sub.id) && (
                      <>
                        {/* {(sub.description || sub.description_ar) && (
                          <div className="row g-3 px-24 py-3 border-bottom">
                            <div className="col-md-6 text-start">
                              {sub.description && (
                                <>
                                  <div className="fw-semibold mb-1">
                                    Description
                                  </div>
                                  <div className="text-secondary-light">
                                    {sub.description}
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="col-md-6 text-end">
                              {sub.description_ar && (
                                <>
                                  <div className="fw-semibold mb-1">الوصف</div>
                                  <div className="text-secondary-light">
                                    {sub.description_ar}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )} */}

                        <ServiceRows
                          services={subServices}
                          onEdit={(s) =>
                            setDialog({ type: "service", service: s })
                          }
                          onDelete={(s) =>
                            setDialog({ type: "deleteService", service: s })
                          }
                        />
                      </>
                    )}
                  </div>
                );
              })}

              {/* Services directly under the category */}
              <div className="border radius-8 overflow-hidden">
                <div className="d-flex align-items-center justify-content-between gap-2 bg-neutral-50 px-24 py-12">
                  <div
                    className="d-flex align-items-center gap-2 flex-grow-1"
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen(DIRECT_KEY)}
                    onClick={() => toggleGroup(DIRECT_KEY)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleGroup(DIRECT_KEY);
                      }
                    }}
                  >
                    <Icon
                      icon={
                        isOpen(DIRECT_KEY)
                          ? "mdi:chevron-down"
                          : "mdi:chevron-right"
                      }
                      className="text-secondary-light text-xl"
                    />
                    <Icon
                      icon="ri:list-unordered"
                      className="text-secondary-light"
                    />
                    <div>
                      <div className="fw-semibold">
                        Directly under {category?.name || "category"}
                      </div>
                      <div className="text-sm text-secondary-light">
                        {directServices.length} services with no subcategory
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="bg-primary-600 bg-hover-primary-700 text-white border border-primary-600 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                    onClick={() => setDialog({ type: "service" })}
                  >
                    <Icon icon="ic:round-plus" />
                  </button>
                </div>
                {isOpen(DIRECT_KEY) && (
                  <ServiceRows
                    services={directServices}
                    onEdit={(s) => setDialog({ type: "service", service: s })}
                    onDelete={(s) =>
                      setDialog({ type: "deleteService", service: s })
                    }
                  />
                )}
              </div>

              {services.length >= 100 && (
                <p className="text-sm text-secondary-light mt-12 mb-0">
                  Showing the first 100 services of this category.{" "}
                  <Link to="/service-catalog">Use the Services list</Link> to
                  see all.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {dialog?.type === "service" && (
        <ServiceFormDialog
          token={token}
          service={dialog.service}
          defaultCategoryId={id}
          defaultSubcategoryId={dialog.subcategoryId}
          lockCategory
          lockSubcategory={!!dialog.subcategoryId}
          onClose={closeDialog}
          onSaved={savedAndReload}
        />
      )}
      {dialog?.type === "subcategory" && (
        <SubcategoryDialog
          token={token}
          categoryId={id}
          subcategory={dialog.subcategory}
          onClose={closeDialog}
          onSaved={savedAndReload}
        />
      )}
      {dialog?.type === "deleteService" && (
        <DeleteServiceDialog
          token={token}
          service={dialog.service}
          onClose={closeDialog}
          onDeleted={savedAndReload}
        />
      )}
      {dialog?.type === "deleteSubcategory" && (
        <DeleteSubcategoryDialog
          token={token}
          subcategory={dialog.subcategory}
          categoryName={category?.name || "the category"}
          onClose={closeDialog}
          onDeleted={savedAndReload}
        />
      )}
    </>
  );
};

/* ---------- read-only services table (all-categories view) ---------- */
const ReadOnlyServiceRows = ({ services }) => {
  if (services.length === 0) {
    return (
      <div className="px-24 py-12 text-secondary-light text-sm">
        No services
      </div>
    );
  }
  return (
    <div className="table-responsive scroll-sm">
      <table className="table bordered-table sm-table mb-0">
        <tbody>
          {services.map((service) => (
            <tr key={service.id}>
              <td style={{ width: 70 }}>{service.id}</td>
              <td>{service.name}</td>
              <td style={{ width: 160 }}>
                {service.durationMinutes
                  ? `${service.durationMinutes} min`
                  : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ---------- all categories (expand / collapse) ---------- */
const AllCategoriesDetail = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set()); // category ids (strings)
  const [details, setDetails] = useState({}); // id -> { subcategories, services, truncated }
  const [serviceCounts, setServiceCounts] = useState({}); // id -> number | null
  const [dialog, setDialog] = useState(null); // { type: "service" | "subcategory" }
  const [reloadKey, setReloadKey] = useState(0);
  const inFlight = useRef(new Set());
  const initialLoad = useRef(true);

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        if (initialLoad.current) setLoading(true);
        const response = await fetch(
          `${API}api/admin/service-categories?limit=100`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await response.json();
        if (!cancelled && response.ok) {
          setCategories(json.data.categories || []);
        }
      } catch (error) {
        console.error("Error loading categories:", error);
        if (!cancelled) toast.error("Failed to load categories");
      } finally {
        if (!cancelled) {
          initialLoad.current = false;
          setLoading(false);
        }
      }
    };
    loadCategories();
    return () => {
      cancelled = true;
    };
  }, [token, reloadKey]);

  // total services per category, for the header line
  useEffect(() => {
    if (categories.length === 0) return;
    let cancelled = false;
    const loadCounts = async () => {
      const entries = await Promise.all(
        categories.map(async (c) => {
          try {
            const response = await fetch(
              `${API}api/admin/services?category_id=${c.id}&page=0&limit=1`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            const json = await response.json();
            if (response.ok) {
              const p = json.data.pagination || {};
              return [String(c.id), p.total_count ?? p.total ?? 0];
            }
          } catch (error) {
            console.error("Error loading service count:", error);
          }
          return [String(c.id), null];
        }),
      );
      if (!cancelled) setServiceCounts(Object.fromEntries(entries));
    };
    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [categories, token]);

  // subcategories + services are loaded the first time a category is expanded
  const loadDetail = useCallback(
    async (categoryId) => {
      const key = String(categoryId);
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [subRes, svcRes] = await Promise.all([
          fetch(
            `${API}api/admin/service-subcategories?category_id=${key}&limit=100`,
            { headers },
          ),
          fetch(`${API}api/admin/services?category_id=${key}&limit=100`, {
            headers,
          }),
        ]);
        const [subJson, svcJson] = await Promise.all([
          subRes.json(),
          svcRes.json(),
        ]);
        if (!subRes.ok || !svcRes.ok) throw new Error("Request failed");
        const services = svcJson.data.services || [];
        setDetails((prev) => ({
          ...prev,
          [key]: {
            subcategories: subJson.data.subcategories || [],
            services,
            truncated: services.length >= 100,
          },
        }));
      } catch (error) {
        console.error("Error loading category details:", error);
        toast.error("Failed to load category details");
      } finally {
        inFlight.current.delete(key);
      }
    },
    [token],
  );

  useEffect(() => {
    expanded.forEach((key) => {
      if (!details[key]) loadDetail(key);
    });
  }, [expanded, details, loadDetail]);

  const isOpen = (id) => expanded.has(String(id));
  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      const k = String(id);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const expandAll = () =>
    setExpanded(new Set(categories.map((c) => String(c.id))));
  const collapseAll = () => setExpanded(new Set());

  // after adding a service / subcategory: refresh the list and any expanded category
  const handleSaved = () => {
    setDialog(null);
    setDetails({});
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-sm btn-light d-flex align-items-center"
            onClick={() => navigate("/service-catalog")}
            title="Back to catalog"
          >
            <Icon icon="ion:arrow-back" />
          </button>
          <div>
            <h6 className="text-md mb-0">All Categories</h6>
            <span className="text-sm text-secondary-light">
              {categories.length} categor{categories.length === 1 ? "y" : "ies"}
            </span>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary-600"
            disabled={categories.length === 0}
            onClick={() => setDialog({ type: "subcategory" })}
          >
            <i className="ri-add-line" /> Add Subcategory
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary-600"
            disabled={categories.length === 0}
            onClick={() => setDialog({ type: "service" })}
          >
            <i className="ri-add-line" /> Add Service
          </button>
        </div>
      </div>

      <div className="card-body p-24">
        {categories.length > 0 && (
          <div className="d-flex justify-content-end gap-2 mb-16">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={expandAll}
            >
              Expand all
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={collapseAll}
            >
              Collapse all
            </button>
          </div>
        )}
        {loading ? (
          <div className="text-center py-4">Loading...</div>
        ) : categories.length === 0 ? (
          <div className="text-center py-4">No categories found</div>
        ) : (
          categories.map((category) => {
            const key = String(category.id);
            const open = isOpen(key);
            const detail = details[key];
            const subCount = (category.subcategories || []).length;
            const svcCount = category.services_count ?? serviceCounts[key];
            const directServices = detail
              ? detail.services.filter((s) => !s.subcategory)
              : [];

            return (
              <div
                key={category.id}
                className="border radius-8 mb-16 overflow-hidden"
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 bg-neutral-50 px-24 py-12">
                  <div
                    className="d-flex align-items-center gap-2 flex-grow-1"
                    style={{ cursor: "pointer" }}
                    role="button"
                    tabIndex={0}
                    aria-expanded={open}
                    onClick={() => toggle(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(key);
                      }
                    }}
                  >
                    <Icon
                      icon={open ? "mdi:chevron-down" : "mdi:chevron-right"}
                      className="text-secondary-light text-xl"
                    />
                    <div>
                      <div className="fw-semibold">{category.name}</div>
                      <div className="text-sm text-secondary-light">
                        {subCount} subcategor{subCount === 1 ? "y" : "ies"}
                        {svcCount != null &&
                          ` · ${svcCount} service${svcCount === 1 ? "" : "s"}`}
                        {category?.description
                          ? ` · ${category?.description}`
                          : ""}
                      </div>
                    </div>
                    <span
                      className={`badge text-sm ${
                        isShowingInBooking(category.is_showing_in_booking)
                          ? "bg-success-focus text-success-600"
                          : "bg-neutral-200 text-secondary-light"
                      }`}
                    >
                      {isShowingInBooking(category.is_showing_in_booking)
                        ? "In booking"
                        : "Hidden"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/service-category/${category.id}`)}
                    className="bg-info-focus bg-hover-primary-200 text-primary-600 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                    title={`View ${category.name}`}
                  >
                    <Icon icon="lucide:eye" className="menu-icon" />
                  </button>
                </div>

                {open &&
                  (!detail ? (
                    <div className="px-24 py-16 text-center text-secondary-light">
                      Loading...
                    </div>
                  ) : (
                    <>
                      {detail.subcategories.map((sub) => {
                        const subServices = detail.services.filter(
                          (s) => String(s.subcategory?.id) === String(sub.id),
                        );
                        return (
                          <div key={sub.id} className="border-top">
                            <div className="d-flex align-items-center gap-2 px-24 py-12">
                              <Icon
                                icon="ri:corner-down-right-line"
                                className="text-secondary-light"
                              />
                              <div>
                                <div className="fw-semibold">{sub.name}</div>
                                <div className="text-sm text-secondary-light">
                                  {sub.services_count ?? subServices.length}{" "}
                                  services
                                  {sub.description
                                    ? ` · ${sub.description}`
                                    : ""}
                                </div>
                              </div>
                              <span
                                className={`badge text-sm ${
                                  isShowingInBooking(sub.is_showing_in_booking)
                                    ? "bg-success-focus text-success-600"
                                    : "bg-neutral-200 text-secondary-light"
                                }`}
                              >
                                {isShowingInBooking(sub.is_showing_in_booking)
                                  ? "In booking"
                                  : "Hidden"}
                              </span>
                            </div>
                            <ReadOnlyServiceRows services={subServices} />
                          </div>
                        );
                      })}

                      <div className="border-top">
                        <div className="d-flex align-items-center gap-2 px-24 py-12">
                          <Icon
                            icon="ri:list-unordered"
                            className="text-secondary-light"
                          />
                          <div>
                            <div className="fw-semibold">
                              Directly under {category.name}
                            </div>
                            <div className="text-sm text-secondary-light">
                              {directServices.length} services with no
                              subcategory
                            </div>
                          </div>
                        </div>
                        <ReadOnlyServiceRows services={directServices} />
                      </div>

                      {detail.truncated && (
                        <p className="text-sm text-secondary-light px-24 py-12 mb-0 border-top">
                          Showing the first 100 services of this category. Open
                          the category to manage them.
                        </p>
                      )}
                    </>
                  ))}
              </div>
            );
          })
        )}
      </div>

      {dialog?.type === "service" && (
        <ServiceFormDialog
          token={token}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      )}
      {dialog?.type === "subcategory" && (
        <SubcategoryDialog
          token={token}
          categories={categories}
          onClose={() => setDialog(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

/* ---------- route entry ----------
   /service-category/all  -> all categories (works with the existing /service-category/:id route)
   /service-category      -> all categories (if you also register this route)
   /service-category/:id  -> one category */
const CategoryDetailLayer = () => {
  const { id } = useParams();
  return id && id !== "all" ? (
    <SingleCategoryDetail key={id} />
  ) : (
    <AllCategoriesDetail />
  );
};

export default CategoryDetailLayer;
